import React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import CpsLoader from '../../common/CpsLoader';
import { LoadingGridPanel } from '../../grid/LoadingGridPanel';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { Tooltip } from '@progress/kendo-react-tooltip';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridItemChangeEvent,
  GridRowClickEvent,
  GridToolbar
} from '@progress/kendo-react-grid';
import { HiddenHeader } from '../../grid/HiddenHeader';
import { HELP_CENTER_VIEW_FIELD } from '../helpCenterManagement/HelpCenterViewDataItem';
import { isEqual } from 'lodash-es';
import { RemoveCell } from '../../grid/customCells/RemoveCell';
import { plusIcon } from '@progress/kendo-svg-icons';
import { RadioButtonCell } from '../../grid/customCells/RadioButtonCell';
import { LinkedCell } from '../../grid/customCells/LinkedCell';
import { DragCell } from '../../grid/customCells/DragCell';
import {
  useGlobalizationContext,
  useTranslation,
  useUiAdminConfigsContext,
  DragContext
} from '@cs-cms/state-management';
import {
  CpsHelpCenterItemState,
  CpsHelpCenterState,
  CPS_UI_ADMIN_CONFIG_TYPE,
  CpsConfirmDialogState,
  CPS_CONFIRM_DIALOG_STATE
} from '@cs-cms/types';

interface HelpCenterDialogState {
  visible: boolean;
  values?: CpsHelpCenterItemState[];
}

interface ValidationErrors {
  [itemId: string]: {
    name?: boolean;
    url?: boolean;
  };
}

export const AdminHelpCenterManagement: React.FC = () => {
  const [dialogState, setDialogState] = React.useState<HelpCenterDialogState>({ visible: false });
  const [editID, setEditID] = React.useState<string>();
  const [activeItem, setActiveItem] = React.useState<CpsHelpCenterItemState | null>(null);
  const [validationErrors, setValidationErrors] = React.useState<ValidationErrors>({});
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  const { isLoading, uiAdminConfigState, upsertUiAdminConfigEntry, tempHelpCenterState, setTempHelpCenterState } =
    useUiAdminConfigsContext();

  const reorder = (dataItem: CpsHelpCenterItemState) => {
    if (activeItem === dataItem || !activeItem) {
      return;
    }

    const reorderedData = [...(tempHelpCenterState?.items || [])];
    const prevIndex = reorderedData.findIndex(item => item.id === activeItem.id);
    const nextIndex = reorderedData.findIndex(item => item.id === dataItem.id);

    reorderedData.splice(prevIndex, 1);
    reorderedData.splice(nextIndex, 0, activeItem);

    setTempHelpCenterState({ items: reorderedData });
  };

  const dragStart = (dataItem: CpsHelpCenterItemState) => {
    setActiveItem(dataItem);
  };

  const edit: { [key: string]: boolean } = {};
  tempHelpCenterState?.items?.forEach(item => {
    edit[item.id] = item.id === editID;
  });

  React.useEffect(() => {
    if (isLoading) {
      return;
    }
    if (uiAdminConfigState.helpCenter && !tempHelpCenterState) {
      const items: CpsHelpCenterItemState[] = (uiAdminConfigState.helpCenter as CpsHelpCenterState).items;
      setTempHelpCenterState({ items });
    }
  }, [isLoading, setTempHelpCenterState, tempHelpCenterState, uiAdminConfigState]);

  const validateItems = (items: CpsHelpCenterItemState[]): ValidationErrors => {
    const errors: ValidationErrors = {};

    items.forEach(item => {
      const itemErrors: { name?: boolean; url?: boolean } = {};

      if (!item.name?.trim()) {
        itemErrors.name = true;
      }

      if (!item.url?.trim()) {
        itemErrors.url = true;
      }

      if (itemErrors.name || itemErrors.url) {
        errors[item.id] = itemErrors;
      }
    });

    return errors;
  };

  const hasValidationErrors = (errors: ValidationErrors): boolean => {
    return Object.keys(errors).length > 0;
  };

  const upsertHelpCenter = (values: CpsHelpCenterItemState[]) => {
    const body = JSON.stringify({ items: values } as CpsHelpCenterState);
    upsertUiAdminConfigEntry(CPS_UI_ADMIN_CONFIG_TYPE.HELP_CENTER, body);
  };

  const handleSubmit = () => {
    if (!tempHelpCenterState?.items) {
      return;
    }

    const errors = validateItems(tempHelpCenterState.items);
    setValidationErrors(errors);

    if (hasValidationErrors(errors)) {
      return;
    }

    setDialogState({ visible: true, values: tempHelpCenterState?.items });
  };

  const handleDialogConfirm = (confirmed: CpsConfirmDialogState) => {
    if (confirmed === CPS_CONFIRM_DIALOG_STATE.YES && dialogState.values) {
      upsertHelpCenter(dialogState.values);
      setValidationErrors({});
      setEditID(undefined);
    }
    setDialogState({ visible: false });
  };

  const addRecord = () => {
    const newItems = tempHelpCenterState?.items?.slice() || [];
    const newItemId = crypto.randomUUID();
    const newItem = {
      id: newItemId,
      name: '',
      url: ''
    };
    newItems.unshift(newItem);
    setTempHelpCenterState({ items: newItems });
    setEditID(newItemId);

    setValidationErrors(prev => ({
      ...prev,
      [newItemId]: {
        name: true,
        url: true
      }
    }));
  };

  const rowClick = (event: GridRowClickEvent) => {
    setEditID(event.dataItem.id);
  };

  const itemChange = (event: GridItemChangeEvent) => {
    const inEditID = event.dataItem.id;
    const field = event.field || '';
    if (tempHelpCenterState?.items) {
      const newItems = tempHelpCenterState.items?.map(item => {
        if (field === HELP_CENTER_VIEW_FIELD.OPEN_ACCESS && event.value) {
          return { ...item, [field]: item.id === inEditID ? event.value : !event.value };
        } else if (item.id === inEditID) {
          return { ...item, [field]: event.value };
        } else {
          return item;
        }
      });
      setTempHelpCenterState({ items: newItems });

      if ((field === HELP_CENTER_VIEW_FIELD.NAME || field === HELP_CENTER_VIEW_FIELD.URL) && event.value?.trim()) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          if (newErrors[inEditID]) {
            delete newErrors[inEditID][field];

            if (!newErrors[inEditID].name && !newErrors[inEditID].url) {
              delete newErrors[inEditID];
            }
          }
          return newErrors;
        });
      }
    }
  };

  const onItemRemove = (cellProps: GridCellProps) => {
    if (tempHelpCenterState?.items) {
      const removeItemIndex = tempHelpCenterState.items.findIndex(dataItem => dataItem.id === cellProps.dataItem?.id);
      if (removeItemIndex !== -1) {
        const newItems = [...tempHelpCenterState.items];
        newItems.splice(removeItemIndex, 1);
        setTempHelpCenterState({ items: newItems });
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[cellProps.dataItem?.id];
          return newErrors;
        });
      }
    }
  };

  const handleUrlCellRender = (cellProps: GridCellProps) => {
    const savedItems = (uiAdminConfigState.helpCenter as CpsHelpCenterState)?.items || [];
    const isNewItem = !savedItems.some(savedItem => savedItem.id === cellProps.dataItem?.id);
    const isInEditMode = cellProps.dataItem.inEdit;

    if (isNewItem || isInEditMode) {
      return (
        <td>
          <input
            type="text"
            className="k-textbox k-input k-input-md k-rounded-md k-input-solid"
            value={cellProps.dataItem[cellProps.field || ''] || ''}
            onChange={e => {
              if (cellProps.onChange) {
                cellProps.onChange({
                  dataItem: cellProps.dataItem,
                  field: cellProps.field,
                  syntheticEvent: e,
                  value: e.target.value,
                  dataIndex: cellProps.dataIndex
                });
              }
            }}
          />
        </td>
      );
    }

    if (cellProps.dataItem.url) {
      return (
        <LinkedCell
          dataItem={cellProps.dataItem}
          field={cellProps.field}
          dataIndex={cellProps.dataIndex}
          id={cellProps.id}
          columnPosition={cellProps.columnPosition}
          onChange={cellProps.onChange}
          className={cellProps.className}
          colSpan={cellProps.colSpan}
          ariaColumnIndex={cellProps.ariaColumnIndex}
          isSelected={cellProps.isSelected}
          style={cellProps.style}
          rowType={cellProps.rowType}
          level={cellProps.level}
          expanded={cellProps.expanded}
          onLinkClicked={() => {
            window.open(cellProps.dataItem.url, '_blank')?.focus();
          }}
        />
      );
    }

    return <td></td>;
  };

  const handleRadioCellRender = (cellProps: GridCellProps) => (
    <RadioButtonCell
      dataItem={cellProps.dataItem}
      field={cellProps.field}
      dataIndex={cellProps.dataIndex}
      id={cellProps.id}
      columnPosition={cellProps.columnPosition}
      onChange={cellProps.onChange}
      className={cellProps.className}
      colSpan={cellProps.colSpan}
      ariaColumnIndex={cellProps.ariaColumnIndex}
      isSelected={cellProps.isSelected}
      style={cellProps.style}
      rowType={cellProps.rowType}
      level={cellProps.level}
      expanded={cellProps.expanded}
      onRadioButtonChange={() => {
        const cellPropsChangeEvent = {
          dataItem: cellProps.dataItem,
          dataIndex: cellProps.dataIndex,
          field: cellProps.field,
          value: true
        } as any;
        if (cellProps.onChange) {
          cellProps.onChange(cellPropsChangeEvent);
        }
      }}
    />
  );

  const handleRemoveCellRender = (cellProps: GridCellProps) => (
    <RemoveCell
      dataItem={cellProps.dataItem}
      field={cellProps.field}
      dataIndex={cellProps.dataIndex}
      id={cellProps.id}
      columnPosition={cellProps.columnPosition}
      onChange={cellProps.onChange}
      className={cellProps.className}
      colSpan={cellProps.colSpan}
      ariaColumnIndex={cellProps.ariaColumnIndex}
      isSelected={cellProps.isSelected}
      style={cellProps.style}
      rowType={cellProps.rowType}
      level={cellProps.level}
      expanded={cellProps.expanded}
      onClick={() => onItemRemove(cellProps)}
    />
  );

  if (!tempHelpCenterState) {
    return <CpsLoader />;
  }

  return (
    <div className="helpCenterManagement">
      {isLoading && <LoadingGridPanel />}

      <DragContext.Provider value={{ reorder, dragStart }}>
        <Tooltip openDelay={500} anchorElement="target" parentTitle={true} position="left">
          <Grid
            data={tempHelpCenterState.items?.map((item: CpsHelpCenterItemState) => ({
              ...item,
              inEdit: item.id === editID
            }))}
            onRowClick={rowClick}
            onItemChange={itemChange}
            dataItemKey="id"
            size="small"
            edit={edit}
            editable={{ mode: 'inline' }}
          >
            <GridToolbar className="helpCenterManagementToolbar">
              <Button
                svgIcon={plusIcon}
                title={translate('custom.settings.adminCenter.helpCenter.addItem')}
                className="k-toolbar-button"
                fillMode="flat"
                type="button"
                onClick={addRecord}
                disabled={tempHelpCenterState.items && tempHelpCenterState.items?.length >= 10}
              >
                {translate('custom.settings.adminCenter.helpCenter.addItem')}
              </Button>
              <Button
                className="k-toolbar-button"
                themeColor="dark"
                title={
                  hasValidationErrors(validationErrors)
                    ? translate('custom.common.incompleteData')
                    : translate('custom.common.save')
                }
                disabled={
                  isEqual(tempHelpCenterState.items, (uiAdminConfigState.helpCenter as CpsHelpCenterState).items) ||
                  hasValidationErrors(validationErrors)
                }
                onClick={handleSubmit}
              >
                {translate('custom.common.save')}
              </Button>
            </GridToolbar>
            <GridColumn
              title={translate('custom.settings.views.gridHeader.order')}
              width={'150px'}
              cells={{ data: DragCell }}
            />
            <GridColumn
              field={HELP_CENTER_VIEW_FIELD.NAME}
              title={translate('custom.settings.views.gridHeader.name')}
              editor="text"
            />
            <GridColumn
              field={HELP_CENTER_VIEW_FIELD.URL}
              title={translate('custom.settings.views.gridHeader.url')}
              editor="text"
              cells={{ data: handleUrlCellRender }}
            />
            <GridColumn
              field={HELP_CENTER_VIEW_FIELD.OPEN_ACCESS}
              title={translate('custom.settings.views.gridHeader.openAccess')}
              width={'150px'}
              cells={{ data: handleRadioCellRender }}
            />
            <GridColumn
              field={HELP_CENTER_VIEW_FIELD.REMOVE}
              width={80}
              cells={{
                headerCell: HiddenHeader,
                data: handleRemoveCellRender
              }}
            />
          </Grid>
        </Tooltip>
      </DragContext.Provider>

      {dialogState.visible && (
        <ConfirmDialog
          message={translate('custom.settings.adminCenter.helpCenter.confirmationMessage')}
          onConfirm={handleDialogConfirm}
        />
      )}
    </div>
  );
};
